<template>
  <div class="page-shell home-page">
    <section class="container home-hero">
      <div class="panel home-hero__main">
        <div v-if="loading" class="home-hero__state status-card">正在加载首页轮播...</div>
        <div v-else-if="errorMessage" class="home-hero__state status-card is-error">
          {{ errorMessage }}
        </div>
        <div v-else-if="carouselList.length" class="home-showcase">
          <aside v-if="heroCategoryList.length" class="home-category-rail">
            <div class="home-category-rail__header">
              <span>课程方向</span>
              <small>COURSE TRACKS</small>
            </div>

            <div class="home-category-rail__list">
              <router-link
                v-for="category in heroCategoryList"
                :key="category.id"
                class="home-category-item"
                :to="{ name: 'courses', query: { category: String(category.id) } }"
              >
                <div class="home-category-item__copy">
                  <strong>{{ category.name }}</strong>
                  <span>{{ formatCategoryPreview(category) }}</span>
                </div>
                <span class="home-category-item__arrow" aria-hidden="true">›</span>
              </router-link>
            </div>
          </aside>

          <div class="home-banner">
            <div ref="heroSwiper" class="swiper home-banner__swiper">
              <div class="swiper-wrapper">
                <button
                  v-for="item in carouselList"
                  :key="item.id"
                  class="swiper-slide home-banner__slide"
                  type="button"
                  @click="handleCarouselClick(item.path)"
                >
                  <img :src="resolveAssetUrl(item.carousel)" :alt="`首页轮播 ${item.id}`">
                </button>
              </div>

              <div ref="heroPagination" class="home-banner__pagination"></div>

            </div>

            <div class="home-banner__badge">
              <strong>新课</strong>
              <span>NEW LESSON</span>
            </div>

            <div class="home-banner__caption">
              <span>精选推荐</span>
              <strong>点击 Banner 直达课程专题或活动页面</strong>
            </div>
          </div>
        </div>
        <div v-else class="home-hero__state status-card is-empty">
          暂时没有轮播图数据。
        </div>
      </div>
    </section>

    <section class="container home-featured-section">
      <div class="panel home-featured">
        <div class="home-featured__bar">
          <div class="home-featured__headline">
            <span class="home-featured__hot">Hot</span>
          </div>

          <div
            v-if="featuredTabs.length"
            class="home-featured__tabs"
            role="tablist"
            aria-label="首页课程推荐标签"
          >
            <button
              v-for="tab in featuredTabs"
              :key="tab.id"
              type="button"
              :class="['home-featured__tab', { 'is-active': activeFeaturedTab === tab.id }]"
              role="tab"
              :aria-selected="activeFeaturedTab === tab.id ? 'true' : 'false'"
              @click="activeFeaturedTab = tab.id"
            >
              {{ tab.label }}
            </button>
          </div>

          <router-link class="home-featured__more" :to="{ name: 'courses' }">
            其他&gt;
          </router-link>
        </div>

        <div v-if="featuredLoading" class="status-card">正在加载课程推荐...</div>
        <div v-else-if="featuredError" class="status-card is-error">
          {{ featuredError }}
        </div>
        <div v-else-if="featuredCourseList.length" class="home-featured__grid">
          <router-link
            v-for="course in featuredCourseList"
            :key="course.id"
            class="home-course-card"
            :to="{ name: 'course-detail', params: { id: String(course.id) } }"
            :style="courseCardStyle(course)"
          >
            <div class="home-course-card__media">
              <img v-if="course.cover" :src="resolveAssetUrl(course.cover)" :alt="course.title">
              <div v-else class="home-course-card__placeholder">
                <span>{{ courseMonogram(course) }}</span>
              </div>

              <span class="home-course-card__badge">
                {{ course.serialize_status_word || '新课' }}
              </span>
            </div>

            <div class="home-course-card__body">
              <h3>{{ course.title }}</h3>
              <div class="home-course-card__footer">
                <span
                  class="home-course-card__category"
                  :title="course.category_two_name || course.category_one_name || '课程专题'"
                >
                  {{ course.category_two_name || course.category_one_name || '课程专题' }}
                </span>
                <span
                  v-if="Number(course.teacher_count) > 0"
                  class="home-course-card__teacher-count"
                >
                  {{ course.teacher_count }} 位教师
                </span>
              </div>
            </div>
          </router-link>
        </div>
        <div v-else class="status-card is-empty">
          当前标签下暂时没有课程，先看看其他分类吧。
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import Swiper from 'swiper'
import { Autoplay, Pagination } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/pagination'

import { fetchHomeIndex } from '@/api/home'
import { fetchCourseList } from '@/api/course'
import { getCourseMonogram, getCourseThemeStyle } from '@/utils/courseTheme'
import { resolveAssetUrl } from '@/utils/format'

const RECOMMENDED_TAB_ID = 'recommended'
const FEATURED_DISPLAY_LIMIT = 8

export default {
  name: 'HomeView',
  data() {
    return {
      loading: true,
      errorMessage: '',
      homeData: {},
      heroSwiper: null,
      featuredLoading: true,
      featuredError: '',
      featuredCourses: [],
      activeFeaturedTab: RECOMMENDED_TAB_ID
    }
  },
  computed: {
    carouselList() {
      return Array.isArray(this.homeData.carousel_list) ? this.homeData.carousel_list : []
    },
    categoryList() {
      return Array.isArray(this.homeData.course_category_list)
        ? this.homeData.course_category_list
        : []
    },
    heroCategoryList() {
      return this.categoryList.slice(0, 7)
    },
    featuredTabs() {
      const categoryTabs = this.categoryList
        .filter(category => category && Number(category.id) > 0 && category.name)
        .map(category => ({
          id: `category-${category.id}`,
          label: category.name,
          type: 'category',
          categoryId: Number(category.id)
        }))

      return [
        {
          id: RECOMMENDED_TAB_ID,
          label: '推荐',
          type: 'recommended'
        },
        ...categoryTabs
      ]
    },
    activeFeaturedTabMeta() {
      return this.featuredTabs.find(tab => tab.id === this.activeFeaturedTab) || this.featuredTabs[0] || null
    },
    featuredCourseList() {
      const activeTab = this.activeFeaturedTabMeta

      if (!activeTab) {
        return []
      }

      const sourceList = activeTab.type === 'category'
        ? this.featuredCourses.filter(course => (
            Number(course.category_one_id) === activeTab.categoryId
            || Number(course.category_two_id) === activeTab.categoryId
          ))
        : this.featuredCourses

      return sourceList.slice(0, FEATURED_DISPLAY_LIMIT)
    }
  },
  created() {
    this.loadHome()
    this.loadFeaturedCourses()
  },
  beforeDestroy() {
    this.destroyHeroSwiper()
  },
  methods: {
    resolveAssetUrl,
    courseCardStyle(course) {
      return getCourseThemeStyle(course)
    },
    courseMonogram(course = {}) {
      return getCourseMonogram(course)
    },
    stripHtml(value = '') {
      return String(value)
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
    },
    formatCourseSummary(course = {}) {
      const plainText = this.stripHtml(course.description)

      if (!plainText) {
        return '围绕真实业务场景设计，适合系统化学习与持续进阶。'
      }

      return plainText.length > 58
        ? `${plainText.slice(0, 58).trim()}...`
        : plainText
    },
    formatCategoryPreview(category) {
      if (!category || !Array.isArray(category.children) || !category.children.length) {
        return '浏览该方向下的全部课程'
      }

      return category.children
        .slice(0, 3)
        .map(item => item.name)
        .filter(Boolean)
        .join(' / ')
    },
    ensureActiveFeaturedTab() {
      if (!this.featuredTabs.length) {
        this.activeFeaturedTab = RECOMMENDED_TAB_ID
        return
      }

      if (!this.featuredTabs.some(tab => tab.id === this.activeFeaturedTab)) {
        this.activeFeaturedTab = this.featuredTabs[0].id
      }
    },
    resolveRouteTarget(path) {
      if (path === '/' || path === '/courses' || path === '/login' || path === '/register' || path === '/reset-password' || path === '/profile') {
        return path
      }

      const matchedCourseId = path.match(/^\/courses?\/(\d+)$/)

      if (matchedCourseId) {
        return {
          name: 'course-detail',
          params: {
            id: matchedCourseId[1]
          }
        }
      }

      return null
    },
    async loadHome() {
      this.loading = true
      this.errorMessage = ''
      this.destroyHeroSwiper()

      try {
        this.homeData = await fetchHomeIndex()
        this.ensureActiveFeaturedTab()
      } catch (error) {
        this.errorMessage = error.message || '首页数据加载失败'
      } finally {
        this.loading = false
        await this.$nextTick()
        this.initHeroSwiper()
      }
    },
    async loadFeaturedCourses() {
      this.featuredLoading = true
      this.featuredError = ''

      try {
        const response = await fetchCourseList({
          page: 0
        })

        this.featuredCourses = Array.isArray(response.data) ? response.data : []
        this.ensureActiveFeaturedTab()
      } catch (error) {
        this.featuredError = error.message || '课程推荐加载失败'
        this.featuredCourses = []
      } finally {
        this.featuredLoading = false
      }
    },
    initHeroSwiper() {
      if (!this.$refs.heroSwiper || !this.carouselList.length) {
        return
      }

      this.destroyHeroSwiper()

      this.heroSwiper = new Swiper(this.$refs.heroSwiper, {
        modules: [Autoplay, Pagination],
        loop: this.carouselList.length > 1,
        speed: 720,
        autoplay: this.carouselList.length > 1
          ? {
              delay: 4200,
              disableOnInteraction: false,
              pauseOnMouseEnter: true
            }
          : false,
        pagination: this.$refs.heroPagination
          ? {
              el: this.$refs.heroPagination,
              clickable: true
            }
          : false
      })
    },
    destroyHeroSwiper() {
      if (this.heroSwiper && !this.heroSwiper.destroyed) {
        this.heroSwiper.destroy(true, true)
      }

      this.heroSwiper = null
    },
    handleCarouselClick(path) {
      if (!path) {
        return
      }

      if (/^https?:\/\//i.test(path)) {
        window.open(path, '_blank', 'noopener')
        return
      }

      const normalizedPath = path.startsWith('/') ? path : `/${path}`
      const routeTarget = this.resolveRouteTarget(normalizedPath)

      if (routeTarget) {
        this.$router.push(routeTarget).catch(() => {})
        return
      }

      window.location.href = normalizedPath
    }
  }
}
</script>

<style scoped lang="scss">
.home-page {
  --home-hero-height: clamp(420px, 56vh, 540px);
  display: grid;
  gap: 28px;
}

.home-hero {
  display: block;
}

.home-hero__main {
  position: relative;
  overflow: hidden;
  padding: 0;
  height: var(--home-hero-height);
}

.home-hero__state {
  display: flex;
  align-items: center;
  justify-content: center;
  height: var(--home-hero-height);
  border: 0;
  border-radius: 0;
}

.home-banner {
  position: relative;
  height: var(--home-hero-height);
}

.home-showcase {
  display: grid;
  grid-template-columns: 270px minmax(0, 1fr);
  height: 100%;
  background: linear-gradient(135deg, #342f4a 0%, #1d1830 100%);
}

.home-category-rail {
  display: grid;
  grid-template-rows: auto 1fr;
  border-right: 1px solid rgba(255, 255, 255, 0.08);
  background: linear-gradient(180deg, #3a3652 0%, #2f2b44 100%);
}

.home-category-rail__header {
  display: grid;
  gap: 6px;
  padding: 22px 22px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.home-category-rail__header span {
  color: rgba(255, 255, 255, 0.96);
  font-size: 18px;
  font-weight: 800;
  letter-spacing: 0.04em;
}

.home-category-rail__header small {
  color: rgba(255, 255, 255, 0.52);
  font-size: 12px;
  letter-spacing: 0.18em;
}

.home-category-rail__list {
  display: grid;
  align-content: stretch;
}

.home-category-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  min-height: 72px;
  padding: 16px 18px 16px 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  transition: background-color 0.2s ease, padding-left 0.2s ease;
}

.home-category-item:hover {
  background: rgba(255, 255, 255, 0.05);
  padding-left: 28px;
}

.home-category-item__copy {
  display: grid;
  gap: 6px;
  min-width: 0;
}

.home-category-item__copy strong {
  color: rgba(255, 255, 255, 0.96);
  font-size: 15px;
  font-weight: 800;
}

.home-category-item__copy span {
  color: rgba(255, 255, 255, 0.72);
  font-size: 13px;
  line-height: 1.45;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.home-category-item__arrow {
  color: rgba(255, 255, 255, 0.62);
  font-size: 24px;
  line-height: 1;
}

.home-banner__swiper {
  height: 100%;
  --swiper-theme-color: #{$color-surface};
  --swiper-pagination-bottom: 30px;
  --swiper-pagination-bullet-size: 11px;
  --swiper-pagination-bullet-horizontal-gap: 6px;
  --swiper-pagination-bullet-inactive-color: rgba(255, 255, 255, 0.48);
  --swiper-pagination-bullet-inactive-opacity: 1;
}

.home-banner__slide {
  position: relative;
  width: 100%;
  height: 100%;
  padding: 0;
  border: 0;
  background: #121212;
}

.home-banner__slide::after {
  content: '';
  position: absolute;
  inset: 0;
  background:
    linear-gradient(90deg, rgba(23, 18, 37, 0.34) 0%, rgba(23, 18, 37, 0.08) 36%, rgba(23, 18, 37, 0.24) 100%),
    linear-gradient(180deg, rgba(18, 18, 18, 0.06) 0%, rgba(18, 18, 18, 0.22) 100%);
  pointer-events: none;
}

.home-banner__slide img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.home-banner__badge {
  position: absolute;
  top: 18px;
  right: 18px;
  z-index: 4;
  display: grid;
  gap: 2px;
  min-width: 112px;
  padding: 14px 18px 12px;
  border: 1px solid rgba(255, 255, 255, 0.32);
  border-radius: 0 18px 18px 18px;
  background: rgba(255, 248, 252, 0.9);
  color: #332c44;
  text-align: center;
  box-shadow: 0 18px 48px rgba(0, 0, 0, 0.12);
}

.home-banner__badge strong {
  font-size: 14px;
  font-weight: 900;
  letter-spacing: 0.08em;
}

.home-banner__badge span {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  opacity: 0.7;
}

.home-banner__caption {
  position: absolute;
  left: 34px;
  bottom: 26px;
  z-index: 4;
  display: grid;
  gap: 8px;
  max-width: 420px;
  padding: 16px 18px;
  border-radius: 18px;
  background: linear-gradient(180deg, rgba(20, 17, 30, 0.16) 0%, rgba(20, 17, 30, 0.56) 100%);
  color: $color-surface;
  backdrop-filter: blur(12px);
}

.home-banner__caption span {
  color: rgba(255, 255, 255, 0.72);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.home-banner__caption strong {
  font-size: 18px;
  line-height: 1.45;
}

.home-banner__pagination {
  position: absolute;
  right: 28px;
  bottom: 22px;
  left: auto;
  z-index: 3;
  width: auto;
}

.home-section {
  margin-top: 0;
}

.home-featured {
  display: grid;
  gap: 28px;
  padding: 30px;
}

.home-featured__bar {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 18px 30px;
}

.home-featured__headline {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.home-featured__hot {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 56px;
  min-height: 30px;
  padding: 0 14px;
  border-radius: 10px 10px 10px 4px;
  background: linear-gradient(135deg, #ff5e69 0%, #ff8264 100%);
  box-shadow: 0 12px 24px rgba(255, 94, 105, 0.22);
  color: $color-surface;
  font-size: 16px;
  font-style: italic;
  font-weight: 900;
  line-height: 1;
}

.home-featured__headline h2 {
  font-size: clamp(32px, 3.4vw, 42px);
  font-style: italic;
  font-weight: 900;
  letter-spacing: 0.02em;
  line-height: 1;
}

.home-featured__tabs {
  display: flex;
  align-items: center;
  gap: 34px;
  min-width: 0;
  padding-bottom: 8px;
  overflow-x: auto;
  scrollbar-width: none;
}

.home-featured__tabs::-webkit-scrollbar {
  display: none;
}

.home-featured__tab {
  position: relative;
  flex: 0 0 auto;
  padding: 0 0 12px;
  border: 0;
  background: transparent;
  color: #2e3440;
  font-size: 18px;
  font-weight: 600;
  white-space: nowrap;
  transition: color 0.2s ease;
}

.home-featured__tab::after {
  content: '';
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  height: 3px;
  border-radius: 999px;
  background: linear-gradient(90deg, $color-primary 0%, $color-accent 100%);
  opacity: 0;
  transform: scaleX(0.45);
  transform-origin: center;
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.home-featured__tab:hover,
.home-featured__tab.is-active {
  color: #111827;
}

.home-featured__tab.is-active {
  font-weight: 800;
}

.home-featured__tab.is-active::after {
  opacity: 1;
  transform: scaleX(1);
}

.home-featured__more {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 36px;
  padding: 0 16px;
  border-radius: 999px;
  background: rgba($color-primary-light, 0.88);
  color: $color-primary;
  font-size: 14px;
  font-weight: 800;
  white-space: nowrap;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;
}

.home-featured__more:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 20px rgba($color-primary, 0.12);
}

.home-featured__grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 20px;
}

.home-course-card {
  display: flex;
  overflow: hidden;
  flex-direction: column;
  border: 1px solid rgba($color-border, 0.92);
  border-radius: 24px;
  background: rgba($color-surface, 0.98);
  box-shadow: 0 18px 42px rgba(15, 23, 42, 0.08);
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}

.home-course-card:hover {
  transform: translateY(-4px);
  border-color: rgba($color-primary, 0.18);
  box-shadow: 0 24px 52px rgba(15, 23, 42, 0.12);
}

.home-course-card__media {
  position: relative;
  height: 196px;
  overflow: hidden;
  background: linear-gradient(135deg, var(--course-theme-primary) 0%, var(--course-theme-secondary) 100%);
}

.home-course-card__media::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(15, 23, 42, 0.04) 0%, rgba(15, 23, 42, 0.24) 100%);
  pointer-events: none;
}

.home-course-card__media img,
.home-course-card__placeholder {
  width: 100%;
  height: 100%;
}

.home-course-card__media img {
  object-fit: cover;
}

.home-course-card__placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.86);
  font-size: 40px;
  font-weight: 900;
  letter-spacing: 0.08em;
}

.home-course-card__badge {
  position: absolute;
  top: 16px;
  left: 16px;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  min-height: 30px;
  padding: 0 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.18);
  color: $color-surface;
  font-size: 12px;
  font-weight: 800;
  backdrop-filter: blur(10px);
}

.home-course-card__body {
  display: flex;
  min-height: 150px;
  padding: 20px 18px 18px;
  flex: 1;
  flex-direction: column;
}

.home-course-card__body h3 {
  display: -webkit-box;
  min-height: 60px;
  margin: 0;
  overflow: hidden;
  color: #1f2937;
  font-size: 20px;
  line-height: 1.5;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.home-course-card__body p {
  color: $color-text-secondary;
  font-size: 14px;
  line-height: 1.72;
  min-height: 72px;
}

.home-course-card__footer {
  display: flex;
  min-width: 0;
  margin-top: auto;
  padding-top: 18px;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border-top: 1px solid rgba($color-border, 0.72);
  color: #8b94a6;
  font-size: 13px;
}

.home-course-card__category {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  color: #606a7b;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.home-course-card__teacher-count {
  flex: 0 0 auto;
  white-space: nowrap;
}

.category-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px;
}

.category-card {
  display: grid;
  gap: 18px;
  padding: 24px;
}

.category-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.category-card__head h3 {
  font-size: 22px;
}

@media (max-width: 960px) {
  .home-page {
    --home-hero-height: clamp(380px, 52vh, 500px);
  }

  .home-featured {
    padding: 24px;
  }

  .home-featured__bar {
    grid-template-columns: 1fr;
    align-items: flex-start;
  }

  .home-featured__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .category-grid {
    grid-template-columns: 1fr;
  }

  .home-showcase {
    grid-template-columns: 1fr;
  }

  .home-category-rail {
    display: none;
  }
}

@media (max-width: 640px) {
  .home-page {
    --home-hero-height: clamp(320px, 46vh, 420px);
    gap: 24px;
  }

  .home-banner__badge {
    top: 14px;
    right: 14px;
    min-width: 92px;
    padding: 12px 14px 10px;
  }

  .home-banner__caption {
    right: 18px;
    left: 18px;
    bottom: 18px;
    max-width: none;
    padding: 14px 16px;
  }

  .home-banner__caption strong {
    font-size: 15px;
  }

  .category-card {
    padding: 18px;
  }

  .category-grid {
    grid-template-columns: 1fr;
  }

  .home-featured {
    gap: 22px;
    padding: 20px;
    min-height: calc(100vh - var(--header-height) - 100px);
  }

  .home-featured__headline {
    gap: 12px;
  }

  .home-featured__hot {
    min-width: 48px;
    min-height: 28px;
    padding: 0 12px;
    font-size: 15px;
  }

  .home-featured__headline h2 {
    font-size: 30px;
  }

  .home-featured__tabs {
    gap: 24px;
  }

  .home-featured__tab {
    padding-bottom: 10px;
    font-size: 16px;
  }

  .home-featured__grid {
    grid-template-columns: 1fr;
    min-height: 400px;
  }

  .home-course-card__media {
    height: 184px;
  }

  .home-course-card__body {
    padding: 16px;
  }

  .home-course-card__body h3 {
    font-size: 18px;
  }

  .home-course-card__body p {
    min-height: 0;
  }

  .home-course-card__footer {
    align-items: center;
    flex-direction: row;
  }

  .home-banner__pagination {
    right: 18px;
    bottom: 18px;
  }
}
</style>
